import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import API from '../../../services/API'

// MUI
import Button from '@mui/material/Button'
import {
  Autocomplete,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import RoleRankingEditor from './Employees/RoleRankingEditor'

const TeamsRoles = () => {
  const [roles, setRoles] = useState([])
  const [teams, setTeams] = useState([])
  const [dbsExpiry, setDbsExpiry] = useState('')
  const [
    drivingLicenceCheckFrequencyMonths,
    setDrivingLicenceCheckFrequencyMonths,
  ] = useState('')
  const [initialValues, setInitialValues] = useState(null)

  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  const mandatoryTeams = [
    'Event Planning',
    'Commercial Training',
    'Governance',
    'Manager',
    'Supervisor',
    'Personnel',
    'Make Ready',
    'Finance',
  ]

  const selects = [
    'roles',
    'teams',
    'additionalTeams',
    'staffGovernanceSettings',
  ]

  useEffect(() => {
    API.get(
      `/settings?select=${selects.map(encodeURIComponent).join(' ')}`,
    ).then((res) => {
      if (res.status === 200) {
        const loadedRoles = res.data.roles ?? []
        const loadedTeams = [
          ...(res.data.teams ?? []),
          ...(res.data.additionalTeams ?? []),
        ]
        const loadedDbsExpiry =
          res.data.staffGovernanceSettings?.dbsCheckExpiry ?? ''
        const loadedDrivingLicenceCheckFrequencyMonths =
          res.data.staffGovernanceSettings
            ?.drivingLicenceCheckFrequencyMonths ?? 12

        setRoles(loadedRoles)
        setTeams(loadedTeams)
        setDbsExpiry(loadedDbsExpiry)
        setDrivingLicenceCheckFrequencyMonths(
          loadedDrivingLicenceCheckFrequencyMonths,
        )

        setInitialValues({
          roles: loadedRoles,
          additionalTeams: loadedTeams.filter(
            (x) => !mandatoryTeams.includes(x),
          ),
          staffGovernanceSettings: {
            dbsExpiry: loadedDbsExpiry,
            drivingLicenceCheckFrequencyMonths:
              loadedDrivingLicenceCheckFrequencyMonths,
          },
        })
      }
    })
  }, [])

  const handleSubmit = () => {
    if (!initialValues) return

    const currentValues = {
      roles,
      additionalTeams: teams.filter((x) => !mandatoryTeams.includes(x)),
      staffGovernanceSettings: {
        dbsExpiry,
        drivingLicenceCheckFrequencyMonths,
      },
    }

    const payload = {}

    if (
      JSON.stringify(currentValues.roles) !==
      JSON.stringify(initialValues.roles)
    ) {
      payload.roles = currentValues.roles
    }

    if (
      JSON.stringify(currentValues.additionalTeams) !==
      JSON.stringify(initialValues.additionalTeams)
    ) {
      payload.additionalTeams = currentValues.additionalTeams
    }

    if (
      JSON.stringify(currentValues.staffGovernanceSettings) !==
      JSON.stringify(initialValues.staffGovernanceSettings)
    ) {
      payload.staffGovernanceSettings = currentValues.staffGovernanceSettings
    }

    if (Object.keys(payload).length === 0) {
      setToast({ message: 'No changes made' })
      return
    }

    API.put('/site-config', payload)
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Settings Updated' })
          setOrgConfig((prev) => ({
            ...prev,
            orgConfig: res.data,
          }))

          setInitialValues(currentValues)
        }
      })
      .catch(() => setError(true))
  }

  return (
    <Stack gap={3} padding={1} divider={<Divider />}>
      <RoleRankingEditor roles={roles} setRoles={setRoles} />
      <Autocomplete
        multiple
        freeSolo
        value={teams}
        options={[
          'Finance',
          'IT',
          'Marketing & Communications',
          'Customer Service',
          'Logistics',
          'Legal',
          'Sales',
          'Research & Development',
          'Health & Safety',
          'Procurement',
        ]}
        onChange={(e, newValue) => {
          const updatedTeams = [...new Set([...mandatoryTeams, ...newValue])]
          setTeams(updatedTeams)
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index })
            return (
              <Chip
                key={key}
                variant="outlined"
                label={option}
                {...chipProps}
                onDelete={
                  !mandatoryTeams.includes(option) && chipProps.onDelete
                }
              />
            )
          })
        }
        fullWidth
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            label="Enabled Teams"
            helperText="Additional teams can be used for Task Groups. Default teams cannot be removed for security reasons. To add a custom team, input the text and press 'Enter'."
          />
        )}
      />

      <FormControl fullWidth>
        <InputLabel>DBS Expiry</InputLabel>
        <Select
          value={dbsExpiry}
          onChange={(e) => setDbsExpiry(e.target.value)}
          label="DBS Expiry"
        >
          <MenuItem value={3}>3 Months</MenuItem>
          <MenuItem value={6}>6 Months</MenuItem>
          <MenuItem value={12}>Annually</MenuItem>
          <MenuItem value={24}>Every 2 Years</MenuItem>
          <MenuItem value={36}>Every 3 Years</MenuItem>
          <MenuItem value={48}>Every 4 Years</MenuItem>
          <MenuItem value={60}>Every 5 Years</MenuItem>
        </Select>
        <FormHelperText>
          How often should DBS checks be carried out?
        </FormHelperText>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Driving Licence Checks</InputLabel>
        <Select
          value={drivingLicenceCheckFrequencyMonths}
          onChange={(e) =>
            setDrivingLicenceCheckFrequencyMonths(e.target.value)
          }
          label="Driving Licence Frequency"
        >
          <MenuItem value={3}>3 Months</MenuItem>
          <MenuItem value={6}>6 Months</MenuItem>
          <MenuItem value={12}>Annually</MenuItem>
          <MenuItem value={24}>Every 2 Years</MenuItem>
          <MenuItem value={36}>Every 3 Years</MenuItem>
          <MenuItem value={48}>Every 4 Years</MenuItem>
          <MenuItem value={60}>Every 5 Years</MenuItem>
        </Select>
        <FormHelperText>
          How often should Driving Licence checks be carried out?
        </FormHelperText>
      </FormControl>

      <Button onClick={handleSubmit} variant="contained" color="success">
        Update
      </Button>
    </Stack>
  )
}

export default TeamsRoles
